import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { TelemedicineRoom, TelemedicineLobby } from '../components/TelemedicineRoom';
import { Video, Calendar, Clock, User, ArrowLeft, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Telemedicina = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appointmentId = searchParams.get('appointment');
  
  const [user, setUser] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inCall, setInCall] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (!userData) {
      navigate('/auth?redirect=/telemedicina' + (appointmentId ? `?appointment=${appointmentId}` : ''));
      return;
    }
    setUser(JSON.parse(userData));
    
    if (appointmentId) {
      loadAppointment();
    } else {
      setLoading(false);
    }
  }, [appointmentId, navigate]);

  const loadAppointment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const appt = response.data;
      
      // Check if appointment is for telemedicine
      if (!appt.is_telemedicine) {
        setError('Questo appuntamento non è una visita in telemedicina');
        return;
      }
      
      // Check if appointment is today and within time window
      const apptDate = new Date(appt.appointment_date);
      const now = new Date();
      const diffMinutes = (apptDate - now) / (1000 * 60);
      
      if (diffMinutes > 15) {
        setError(`L'appuntamento inizia alle ${apptDate.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'})}. Potrai accedere 15 minuti prima.`);
      } else if (diffMinutes < -60) {
        setError('L\'appuntamento è terminato');
      }
      
      setAppointment({
        id: appt.id,
        date: apptDate.toLocaleDateString('it-IT'),
        time: apptDate.toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}),
        doctor: appt.doctor_name || 'Dott. Prime Vision',
        reason: appt.reason || 'Consulto Telemedicina',
        patientName: appt.patient_name
      });
    } catch (err) {
      console.error('Error loading appointment:', err);
      setError('Impossibile caricare i dettagli dell\'appuntamento');
    }
    setLoading(false);
  };

  const handleJoinCall = () => {
    setInCall(true);
    // Log telemedicine start
    const token = localStorage.getItem('token');
    axios.post(`${API}/telemedicine/start`, { 
      appointment_id: appointmentId 
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(console.error);
  };

  const handleEndCall = () => {
    setInCall(false);
    toast.success('Consulto terminato');
    
    // Log telemedicine end
    const token = localStorage.getItem('token');
    axios.post(`${API}/telemedicine/end`, { 
      appointment_id: appointmentId 
    }, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(console.error);
    
    // Redirect to dashboard or feedback
    navigate(user?.role === 'patient' ? '/dashboard/patient' : '/dashboard/doctor');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-[#2A9FCB]/70">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{background: 'linear-gradient(135deg, #daeef9 0%, #f0f7fc 50%, #d4e8f5 100%)'}}>
      {!inCall && <Navbar />}
      
      <main className={`flex-1 ${inCall ? '' : 'pt-24 pb-12'}`}>
        {inCall ? (
          // Video Call View
          <div className="h-screen w-full">
            <TelemedicineRoom
              appointmentId={appointmentId}
              patientName={user?.role === 'doctor' ? appointment?.patientName : null}
              doctorName={user?.role === 'patient' ? appointment?.doctor : null}
              onEnd={handleEndCall}
            />
          </div>
        ) : (
          // Lobby View
          <div className="container mx-auto px-4">
            {/* Back button */}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[#2A9FCB]/80 hover:text-gray-900 mb-6"
            >
              <ArrowLeft className="w-5 h-5" />
              Torna indietro
            </button>

            {error ? (
              // Error State
              <div className="max-w-lg mx-auto text-center py-12">
                <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-xl font-bold text-[#2A9FCB] mb-2">Impossibile Accedere</h2>
                <p className="text-[#2A9FCB]/80 mb-6">{error}</p>
                <button
                  onClick={() => navigate('/dashboard/patient')}
                  className="px-6 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                >
                  Vai alla Dashboard
                </button>
              </div>
            ) : !appointmentId ? (
              // No Appointment Selected - Show Upcoming Telemedicine
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Video className="w-10 h-10 text-green-600" />
                  </div>
                  <h1 className="text-3xl font-bold" style={{color: '#2A9FCB'}}>Telemedicina</h1>
                  <p className="text-[#2A9FCB]/70 mt-2">Consulta il tuo medico comodamente da casa</p>
                </div>

                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-xl font-semibold mb-4">Come funziona</h2>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">1. Prenota</h3>
                        <p className="text-[#2A9FCB]/70 text-sm">Seleziona "Telemedicina" quando prenoti il tuo appuntamento</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">2. Preparati</h3>
                        <p className="text-[#2A9FCB]/70 text-sm">15 minuti prima riceverai un link per accedere</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Video className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-medium">3. Connettiti</h3>
                        <p className="text-[#2A9FCB]/70 text-sm">Clicca sul link e inizia la videochiamata con il medico</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <button
                      onClick={() => navigate('/booking')}
                      className="w-full py-4 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <Calendar className="w-5 h-5" />
                      Prenota un Consulto Telemedicina
                    </button>
                  </div>
                </div>

                {/* Benefits */}
                <div className="mt-8 grid md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <p className="text-3xl mb-2">🏠</p>
                    <h3 className="font-semibold">Da Casa</h3>
                    <p className="text-sm text-[#2A9FCB]/70">Nessuno spostamento necessario</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <p className="text-3xl mb-2">⏱️</p>
                    <h3 className="font-semibold">Risparmia Tempo</h3>
                    <p className="text-sm text-[#2A9FCB]/70">Niente code o attese</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 text-center shadow-sm">
                    <p className="text-3xl mb-2">👨‍⚕️</p>
                    <h3 className="font-semibold">Stesso Medico</h3>
                    <p className="text-sm text-[#2A9FCB]/70">La stessa qualità del consulto in studio</p>
                  </div>
                </div>
              </div>
            ) : (
              // Lobby with device check
              <TelemedicineLobby
                appointment={appointment}
                onJoin={handleJoinCall}
                onCancel={() => navigate(-1)}
              />
            )}
          </div>
        )}
      </main>

      {!inCall && <Footer />}
    </div>
  );
};

export default Telemedicina;
